import '@backstage/cli/asset-types';
import ReactDOM from 'react-dom/client';
import App from './App';
import '@backstage/ui/css/styles.css';

// ODX Design System — core-theme provides all CSS classes (.odx-header, .odx-button, .odx-logo, etc.)
import '@odx/ui/core-theme.css';
import '@odx/design-tokens/css';
// ISDP CUSTOM: the line above is tree-shaken (broken `sideEffects` path in @odx/design-tokens),
// so the token :root{} never loads. Import a vendored local copy — a local CSS import is never
// tree-shaken — to guarantee var(--odx-spacing-*/--odx-size-*/--odx-border-radius-*) resolve.
import './odx-tokens.css';
import '@odx/design-tokens/fonts';
import '@odx/icons'; // registers <odx-icon> custom element
// Explicitly import each icon to ensure registerAsset() side-effects run
// (import '@odx/icons/core' re-exports get tree-shaken by the bundler)
import '@odx/icons/core/favorite';
import '@odx/icons/core/favorite-solid';
import '@odx/icons/core/search';
import '@odx/icons/core/settings';
import '@odx/icons/core/user';
import '@odx/icons/core/chevron-down';
import '@odx/icons/core/chevron-right';
import '@odx/icons/core/tile-menu';
import '@odx/icons/core/bar-chart';
import '@odx/icons/core/link';
import '@odx/icons/core/aggregation';
import '@odx/icons/core/menu';
import '@odx/icons/core/document';
import '@odx/icons/core/plus';
import '@odx/icons/core/info';
import '@odx/icons/core/media-play';
import '@odx/icons/core/config';
import '@odx/icons/core/help';
import '@odx/icons/core/cloud-upload';
import '@odx/icons/core/picture';
import '@odx/icons/core/bookmark';
import '@odx/icons/core/notification';
import '@odx/icons/core/time';
import '@odx/icons/core/edit';
import '@odx/icons/core/error-filled';
import '@odx/icons/core/reload';
import '@odx/icons/core/mail';
import '@odx/icons/core/phone';
import '@odx/icons/core/products';
import '@odx/icons/core/shoppingcart';
import '@odx/icons/core/filter';
import '@odx/icons/core/check';
import '@odx/icons/core/close';
import '@odx/icons/core/warning';
import '@odx/icons/core/lock'; // Partner Program gate (Contact/registration sidebar)
import '@odx/icons/core/dashboard';
import '@odx/icons/core/information-security';
import '@odx/icons/core/globe';
import '@odx/icons/core/cloud';
import '@odx/icons/core/object';
import '@odx/icons/core/download';
import '@odx/icons/core/link-internal';
import '@odx/icons/core/arrow-right';
import '@odx/icons/core/chevron-left';
import '@odx/icons/core/link-external';
import '@odx/icons/core/history';
import '@odx/icons/core/service';
import '@odx/icons/core/toolbox';
import '@odx/icons/core/night-mode';
import '@odx/icons/core/brightness';
// Section hub / overview page icons
import '@odx/icons/core/map';
import '@odx/icons/core/training';
import '@odx/icons/core/colorpicker';
import '@odx/icons/core/config-edit';
import '@odx/icons/core/ai-spark';
import '@odx/icons/core/cloud-ok';
import '@odx/icons/core/group';
import '@odx/icons/core/folder-team';
// Category-taxonomy section icons (isdp-common/categories → CATEGORY_ICONS in
// LandscapePage). Every category's icon must be registered here or odx-icon
// renders blank; the rest of the 18 reuse icons already imported above/below.
import '@odx/icons/core/networking';
import '@odx/icons/core/request-list';
import '@odx/icons/core/chat-agent';
import '@odx/icons/core/tablet';
import '@odx/icons/core/visual-inspection';
import '@odx/icons/core/code-of-conduct';
// Safety & Medical domain icons
import '@odx/icons/safety/fire';
import '@odx/icons/safety/sensors';
import '@odx/icons/safety/breathing-apparatus';
import '@odx/icons/safety/monitoring';
import '@odx/icons/safety/datahub';
import '@odx/icons/safety/toxic';
import '@odx/icons/medical/device';
import '@odx/icons/medical/neonatal-patient';
import '@odx/icons/core/home';
// DevSecOps Hub icons
import '@odx/icons/core/folder';
import '@odx/icons/core/delete';
import '@odx/icons/core/copy-content';
import '@odx/icons/core/view';
import '@odx/icons/core/view-off';
import '@odx/icons/core/arrow-left';
import '@odx/web-components/load';
import '@odx/web-components/styles';
import '@odx/foundation';
import '@odx/foundation/styles';

// Shared public-page entrance animations (keyframes + scroll-reveal base).
import './animations.css';

// MUST be last — overrides ODX html{font-size:62.5%} and recalculates base variables
import './odx-overrides.css';

// Set initial color-scheme before React hydrates. LIGHT is the default for all
// users — we deliberately do NOT auto-follow the OS `prefers-color-scheme`.
// Dark mode applies only when the user has explicitly chosen it (a stored
// preference). The ThemeProviderWrapper / useDarkMode hooks take over on mount.
(() => {
  const root = document.documentElement;
  // Two explicit-preference storage keys exist; default is light when neither set:
  //  - Backstage appThemeApi key ('theme'), value e.g. "draeger-dark"
  //  - public-shell key ('devhub-dark-mode'), value "true" / "false"
  let preferDark = false;
  const bsTheme = localStorage.getItem('theme');
  if (bsTheme) {
    preferDark = bsTheme === 'draeger-dark';
  }
  const publicPref = localStorage.getItem('devhub-dark-mode');
  if (publicPref !== null) {
    preferDark = publicPref === 'true';
  }

  if (preferDark) {
    root.style.colorScheme = 'dark';
    // odx-theme-dark MUST be on <body> (a descendant of :root)
    // because ODX uses :root:has(.odx-theme-dark) which checks descendants
    document.body?.classList.add('odx-dark-mode', 'odx-theme-dark');
    document.body?.classList.remove('odx-light-mode');
  } else {
    root.style.colorScheme = 'light';
    document.body?.classList.add('odx-light-mode');
    document.body?.classList.remove('odx-dark-mode', 'odx-theme-dark');
  }
})();

// ISDP CUSTOM: Public Frontstage — render outside the Backstage app entirely.
// Backstage's AppRouter requires authentication and does static route discovery,
// so public pages must bypass it at the entry-point level for ANONYMOUS visitors.
//
// Two-worlds routing (see docs/PARTNER_RESTRUCTURE.md): a public path loads the
// lightweight anonymous PublicApp ONLY when no one is signed in. Once signed in
// (the readable `devhub-signed-in` flag is set), even public paths load the main
// app, which renders the external Partner Portal in-app for partners — so identity
// and fetchApiRef (token minting, sign-out) are available. Everything else always
// loads the main app.
//
// RELEASE SCOPE: when the partnerPortal feature is off there is NO anonymous surface at
// all — every path loads the main app, whose AppRouter puts an unauthenticated visitor on
// the sign-in screen. The public-path branch below is what the flag switches back on.
// This runs before React mounts, so it reads the flag from the config block the app-backend
// (and the CLI dev server) injects into index.html rather than from configApi.
import { featureEnabledFromInjectedConfig } from '@internal/plugin-isdp-common';
import { isSignedInFlagSet } from './components/auth/portalSession';

// The portal's public URLs — must match PublicApp's route table (a path missing here
// boots the main app on a full page load and shows the sign-in screen instead).
// '/getting-started' also covers '/getting-started/onboarded' via the prefix test below.
const PUBLIC_PATHS = [
  '/products',
  '/getting-started',
  '/partner-program',
  '/contact',
];
const isPublicPath = PUBLIC_PATHS.some(
  p => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`),
);
const loadAnonymousPortal =
  featureEnabledFromInjectedConfig('partnerPortal') &&
  isPublicPath &&
  !isSignedInFlagSet();

if (loadAnonymousPortal) {
  import('./PublicApp').then(({ default: PublicApp }) => {
    ReactDOM.createRoot(document.getElementById('root')!).render(<PublicApp />);
  });
} else {
  ReactDOM.createRoot(document.getElementById('root')!).render(<App />);
}
